import { Box, CircularProgress, Typography } from "@material-ui/core";
import { ParentSize } from "@visx/responsive";
import { useMemo } from "react";
import { kebabCase } from "lodash";

import useQueryState from "../../../../../../../../hooks/useQueryState";
import Chart from "../../../../../../../../Components/Chart";

import {
  colors,
  getAnomalyColor,
} from "../../../../../../../../helpers/anomaliesHelper";
import useTimeseriesFinancial from "../../../../../../../../hooks/useTimeseriesFinancial";
import { formatNumber } from "../../../../../../../../utils/numberFormat";

type TimeseriesPayoutProps = {
  season: string;
};
type SeasonKey = 1 | 2;

const TimeseriesPayout = ({ season }: TimeseriesPayoutProps) => {
  const {
    postFinancialTimeseries: { data: payoutTimeseries, isLoading },
  } = useTimeseriesFinancial({ enabled: true });
  const nextSeason = (Number(season) + 1) as SeasonKey;

  const {
    queryState: {
      ui: { mapSidebar, mainSidebar },
      payout: { comparisonFocus, seasons },
    },
  } = useQueryState();

  const data = useMemo(() => {
    if (!payoutTimeseries?.data?.length) return [];

    return payoutTimeseries.data.filter(
      (item: any) => item.seasonId - 1 === Number(season),
    );
  }, [payoutTimeseries?.data, season]);

  const years = useMemo(() => {
    if (!data?.length) return [];

    const yearSet = new Set<number>();

    data.forEach((season: any) => {
      season.payouts?.forEach((p: any) => {
        p.data?.forEach(([year]: [number, number]) => {
          yearSet.add(year);
        });
      });
    });

    return Array.from(yearSet).sort((a, b) => a - b);
  }, [data]);

  const series = useMemo(() => {
    if (!data?.length || !years.length) return [];

    const rawSeries = data.flatMap((season: any) =>
      season.payouts.map((payout: any) => {
        const anomaly = payout.displayName?.split(" - ")?.[1];
        const indicator = payout.displayName;

        const name = comparisonFocus === 0 ? `${anomaly} Anomaly` : indicator;

        return {
          name,
          type: "bar",
          data: years.map((year) => {
            const found = payout.data.find(
              ([y]: [number, number]) => y === year,
            );
            return found ? found[1] : 0;
          }),
          meta: {
            anomaly,
            indicator,
          },
        };
      }),
    );

    const sorted = rawSeries.sort((a: any, b: any) => {
      const A = a.name ?? "";
      const B = b.name ?? "";

      const isHybridA = A === "Hybrid Anomaly";
      const isHybridB = B === "Hybrid Anomaly";

      if (isHybridA && !isHybridB) return 1;
      if (!isHybridA && isHybridB) return -1;

      return A.localeCompare(B);
    });

    return sorted.map((s: any, index: number) => ({
      ...s,
      itemStyle: {
        color:
          comparisonFocus === 0
            ? getAnomalyColor(kebabCase(s.meta.anomaly))
            : colors[index % colors.length],
      },
    }));
  }, [data, years, comparisonFocus]);

  const option = useMemo(
    () => ({
      tooltip: {
        show: true,
        axisPointer: { type: "shadow" },
        valueFormatter: (value: number | string) =>
          typeof value === "number" ? formatNumber(value) : value,
      },
      grid: {
        left: 40,
        right: 10,
        bottom: 50,
        top: 40,
      },
      legend: {
        padding: [0, 0, 0, 60],
        type: "scroll",
        orient: "horizontal",
        data: series.map((s: any) => s.name),
        itemWidth: 19,
        itemHeight: 11,
        itemGap: 20,
        pageButtonPosition: "end",
        pageIconSize: 12,
        pageTextStyle: { fontSize: 11 },
      },
      xAxis: {
        type: "category",
        data: years,
        // splitArea: {
        //   show: true,
        //   areaStyle: { color: ["#F5F5F5", "#FFFFFF"] },
        // },
        // splitLine: { show: true },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value: number) => formatNumber(value),
        },
      },
      series,
    }),
    [series, years],
  );

  if (isLoading) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        my={10}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (
    !payoutTimeseries?.data?.length ||
    payoutTimeseries?.data?.[season]?.payouts.length === 0
  ) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        py={5}
      >
        <Typography variant="caption" color="textSecondary">
          There is no data to display
        </Typography>
      </Box>
    );
  }

  return (
    <ParentSize>
      {({ width, height }) => {
        if (!width || !height) return null;
        return (
          <Chart
            width={`calc(100vw - ${mainSidebar ? "370px" : "170px"} - ${
              mapSidebar ? "370px" : "40px"
            }`}
            height={275}
            option={option}
            onLegendSelectChanged={() => {}}
          />
        );
      }}
    </ParentSize>
  );
};

export default TimeseriesPayout;
